import Select, { type SingleValue } from 'react-select';
import translations from '../../../../../../translations';
import type {
    ContentType,
    ValueElementIdAPI,
    ValueElementIdReferenceAPI,
} from '../../../../../../types/value';
import { isNullOrEmpty } from '../../../../../../utils/guard';
import { getSharedStyles } from '../../../../../../utils/select';
import FormGroup from '../../../../../generic/FormGroup';
import ValueSelectorModal from '../../../../../values/selector/ValueSelectorModal';
import { PAGE_OBJECT_REFERENCE_TYPE_OPTIONS, TOGGLE_CATEGORY_OPTIONS } from '../../../../constants';

interface Props {
    selectedConfigType?: string | undefined;
    componentOptions?: { label: string; value: string }[];
    containerOptions?: { label: string; value: string }[];
    propertyOptions?: { label: string; value: string }[];
    selectedComponent?: { label: string; value: string } | null;
    selectedContainer?: { label: string; value: string } | null;
    selectedProperty?: { label: string; value: string } | null;
    selectedValue?: ValueElementIdAPI | null;
    updateSelectedComponent?: (
        newComponent: SingleValue<{ label: string; value: string }> | null,
    ) => void;
    updateSelectedContainer?: (
        newContainer: SingleValue<{ label: string; value: string }> | null,
    ) => void;
    updateSelectedProperty?: (newProperty: SingleValue<{ label: string; value: string }>) => void;
    updateSelectedValue?: (value: ValueElementIdReferenceAPI | null) => void;
    hasSubmitted: boolean;
    identifier: string;
    contentTypeFilter?: ContentType | null | undefined;
    container?: HTMLElement | null | undefined;
}

const ComponentOrValueConfig = ({
    selectedConfigType,
    componentOptions,
    containerOptions,
    propertyOptions,
    selectedComponent,
    selectedContainer,
    selectedProperty,
    selectedValue,
    updateSelectedComponent,
    updateSelectedContainer,
    updateSelectedProperty,
    updateSelectedValue,
    hasSubmitted,
    identifier, // used to generate unique labels, input and test IDs
    contentTypeFilter,
    container,
}: Props) => {
    const showContainer = selectedConfigType === PAGE_OBJECT_REFERENCE_TYPE_OPTIONS['CONTAINER'];
    const showComponent =
        selectedConfigType === undefined ||
        selectedConfigType === TOGGLE_CATEGORY_OPTIONS['COMPONENT'] ||
        selectedConfigType === 'ONLY_COMPONENT';
    const showProperty =
        showContainer ||
        selectedConfigType === undefined ||
        selectedConfigType === TOGGLE_CATEGORY_OPTIONS['COMPONENT'] ||
        selectedConfigType === 'ONLY_PROPERTY';
    const showValue = !(showComponent || showProperty);

    return (
        <>
            {showContainer && updateSelectedContainer && containerOptions && (
                <FormGroup
                    labelId={`${identifier}-container-label`}
                    label="Components within container"
                    htmlFor={`${identifier}-container`}
                    isValid={!isNullOrEmpty(selectedContainer)}
                    validationMessage={
                        translations.PAGE_BUILDER_field_is_required_validation_message
                    }
                    showValidation={hasSubmitted}
                    isRequired
                >
                    <Select
                        inputId={`${identifier}-container`}
                        className="select-field"
                        styles={getSharedStyles<(typeof containerOptions)[number]>()}
                        options={containerOptions}
                        onChange={(newContainer) => updateSelectedContainer(newContainer)}
                        placeholder="Select a container"
                        value={selectedContainer || null}
                        noOptionsMessage={() => 'No results found'}
                        isClearable
                    />
                </FormGroup>
            )}
            {showComponent && updateSelectedComponent && componentOptions && (
                <FormGroup
                    labelId={`${identifier}-component-label`}
                    label="Component"
                    htmlFor={`${identifier}-component`}
                    isValid={!isNullOrEmpty(selectedComponent)}
                    validationMessage={
                        translations.PAGE_BUILDER_field_is_required_validation_message
                    }
                    showValidation={hasSubmitted}
                    isRequired
                >
                    <Select
                        inputId={`${identifier}-component`}
                        className="select-field"
                        styles={getSharedStyles<(typeof componentOptions)[number]>()}
                        options={componentOptions}
                        onChange={(newComponent) => updateSelectedComponent(newComponent)}
                        placeholder="Select a component"
                        value={selectedComponent || null}
                        noOptionsMessage={() => 'No results found'}
                        isClearable
                    />
                </FormGroup>
            )}
            {showProperty && updateSelectedProperty && propertyOptions && (
                <FormGroup
                    labelId={`${identifier}-property-label`}
                    label="Property"
                    htmlFor={`${identifier}-property`}
                    isValid={!isNullOrEmpty(selectedProperty)}
                    validationMessage={
                        translations.PAGE_BUILDER_field_is_required_validation_message
                    }
                    showValidation={hasSubmitted}
                    isRequired
                >
                    <Select
                        inputId={`${identifier}-property`}
                        className="select-field"
                        styles={getSharedStyles<(typeof propertyOptions)[number]>()}
                        value={selectedProperty || null}
                        options={propertyOptions}
                        onChange={(newProperty) => updateSelectedProperty(newProperty)}
                        placeholder="Select a component property"
                        noOptionsMessage={() => 'No results found'}
                        isClearable
                    />
                </FormGroup>
            )}
            {showValue && updateSelectedValue && (
                <FormGroup
                    labelId={`${identifier}-value-label`}
                    label="Value"
                    htmlFor={`${identifier}-value`}
                    isValid={!isNullOrEmpty(selectedValue)}
                    validationMessage={
                        translations.PAGE_BUILDER_field_is_required_validation_message
                    }
                    showValidation={hasSubmitted}
                    isRequired
                >
                    <ValueSelectorModal
                        onChangeAsValueReference={updateSelectedValue}
                        value={selectedValue || null}
                        contentType={contentTypeFilter || null}
                        includeSystemValues={true}
                        container={container}
                    />
                </FormGroup>
            )}
        </>
    );
};

export default ComponentOrValueConfig;
